/* Landing-only purchase rules. Shopify remains the authority for every cart amount. */
(() => {
  const money = (value) => value !== '' && value != null && /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const purchasable = (variant) => !!variant && variant.available === true && variant.purchasable === true;
  function offer(data) {
    const paid = data.paidQuantity === '' ? 1 : Number(data.paidQuantity);
    const free = data.freeQuantity === '' ? 0 : Number(data.freeQuantity);
    return {
      slot: data.slot, key: data.offerKey || data.offerId || data.slot,
      paid, free, total: paid + free, includesGift: data.includesGift === 'true',
      price: money(data.offerPrice), compare: money(data.comparePrice),
      discount: (data.discountCode || '').trim(),
      valid: Number.isSafeInteger(paid) && paid >= 1 && Number.isSafeInteger(free) && free >= 0 && paid + free <= 250,
    };
  }
  function initialSelections(activeOffer, variants) {
    const available = variants.filter(purchasable);
    return Array.from({ length: activeOffer?.valid && activeOffer.total > 1 ? activeOffer.total : 0 },
      (_, index) => available[index % available.length]?.id || '');
  }
  function watches(activeOffer, mainId, selections, variants) {
    const ids = activeOffer?.total > 1 ? selections : [mainId];
    return ids.map((id, index) => ({
      ...variants.find((variant) => variant.id === id), id,
      position: index + 1, role: index < (activeOffer?.paid || 1) ? 'paid' : 'free',
    }));
  }
  function pricing(activeOffer, useProductPricing, selectedWatches) {
    // HeroForce displays the first selected watch in product-price mode.
    const variant = selectedWatches[0];
    const configured = !useProductPricing && activeOffer?.price !== null && activeOffer?.price !== undefined;
    const price = configured ? activeOffer.price : variant?.price;
    const compare = configured ? activeOffer.compare : variant?.compare;
    return { price, compare: compare > price ? compare : null, configured, variantId: variant?.id };
  }
  function giftError(gift) {
    return !gift || !/^\d+$/.test(gift.id) || !purchasable(gift) || gift.price !== 0;
  }
  function validate(activeOffer, selectedWatches, gifts, configuredGifts) {
    if (activeOffer && !activeOffer.valid) return 'This offer has an invalid watch quantity. Please contact us.';
    if (selectedWatches.length !== (activeOffer?.total || 1) || selectedWatches.some((watch) => !purchasable(watch))) {
      return 'Please select an available variant for every watch.';
    }
    if (activeOffer?.includesGift && (configuredGifts.length !== (activeOffer.total === 1 ? 1 : 3) || configuredGifts.some(giftError))) {
      return 'This offer needs available, zero-priced gift variants. Please contact us before purchasing.';
    }
    if (gifts.some(giftError)) return 'An included gift is unavailable or is not free.';
    if (selectedWatches.some((watch) => watch.role === 'free' && watch.price !== 0) && !activeOffer?.discount) {
      return 'This offer needs a Shopify discount that makes the bonus watches free. Please contact us.';
    }
    return '';
  }
  function watchItems(activeOffer, selectedWatches, group, pricingMode) {
    return selectedWatches.map((watch) => ({
      id: watch.id, quantity: 1,
      properties: {
        _landing_group: group,
        _landing_position: String(watch.position), _landing_role: watch.role,
        ...(activeOffer ? {
          _landing_offer: activeOffer.key, _landing_option: activeOffer.slot,
          _landing_quantity_locked: 'true',
          ...(!pricingMode && activeOffer.price !== null ? { _landing_price_ceiling: String(activeOffer.price) } : {}),
        } : {}),
      },
    }));
  }
  function discountCodes(cart) {
    const codes = (cart.discount_codes || []).map((entry) => entry.code);
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((line) => (line.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
    ];
    applications.filter((app) => app?.type === 'discount_code').forEach((app) => codes.push(app.title));
    return [...new Map(codes.filter((code) => typeof code === 'string' && code.trim()).map((code) => [code.toLowerCase(), code])).values()];
  }
  function mergedDiscount(cart, code) {
    if (!code?.trim()) return null; // Empty would clear the customer's discounts.
    return [...new Map([...discountCodes(cart), code.trim()].map((value) => [value.toLowerCase(), value])).values()].join(',');
  }
  function verifyCart(cart, group, selectedWatches, gifts) {
    const lines = cart.items.filter((line) => line.properties?._landing_group === group);
    if (lines.length !== selectedWatches.length + gifts.length || lines.some((line) => line.quantity !== 1)) {
      return 'Shopify did not return the complete offer. The offer could not be confirmed.';
    }
    for (const watch of selectedWatches) {
      const line = lines.find((item) => item.properties._landing_position === String(watch.position) && item.properties._landing_gift !== 'true');
      if (!line || String(line.variant_id || line.id) !== watch.id) return 'The cart watch selections could not be confirmed.';
    }
    const parent = lines.find((line) => line.properties._landing_position === '1' && line.properties._landing_gift !== 'true');
    for (const [index, gift] of gifts.entries()) {
      const child = lines.find((line) => line.properties._landing_gift_position === String(index + 1));
      if (!child || String(child.variant_id || child.id) !== gift.id || child.parent_relationship?.parent_key !== parent?.key) {
        return 'Shopify could not attach the included gifts to the selected watch.';
      }
    }
    // A new discount must not make an earlier promised free line chargeable.
    for (const line of cart.items.filter((item) => item.properties?._landing_group)) {
      if (!Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0) return 'Shopify cart pricing could not be verified.';
      if ((line.properties._landing_role === 'free' || line.properties._landing_gift === 'true') && line.final_line_price !== 0) {
        return 'The Shopify discount did not make every promised free item free. Please contact us about this offer.';
      }
    }
    const groups = new Map();
    cart.items.filter((line) => line.properties?._landing_group).forEach((line) => {
      const id = line.properties._landing_group;
      const entry = groups.get(id) || { total: 0, ceiling: null };
      entry.total += line.final_line_price;
      entry.ceiling = money(line.properties._landing_price_ceiling) ?? entry.ceiling;
      groups.set(id, entry);
    });
    if ([...groups.values()].some((entry) => entry.ceiling !== null && entry.total > entry.ceiling)) {
      return 'Shopify is charging more than the configured offer price. Please contact us about this offer.';
    }
    return '';
  }
  function nextDeadline(stored, now) {
    return Number.isFinite(stored) && stored > now ? stored : now + 86400000;
  }
  const api = { money, purchasable, offer, initialSelections, watches, pricing, validate, watchItems, discountCodes, mergedDiscount, verifyCart, nextDeadline };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.LandingPurchaseEngine = api;
})();
